import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Compass, LifeBuoy, MessageCircle, Plus, Sparkles, TrendingUp, Users } from "lucide-react";
import api from "@/lib/api";
import ServerCard from "@/components/ServerCard";
import Marquee from "@/components/Marquee";
import SEO, { SITE_URL, SITE_NAME } from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ servers: 0, users: 0, total_members: 0 });
  const { user, loginWithDiscord } = useAuth();
  const login = loginWithDiscord;
  const navigate = useNavigate();

  useEffect(() => {
    // Fire each request independently so a single slow/failed endpoint
    // doesn't blank out the whole page (previously used Promise.all — one
    // failure caused Servers/Users to stick at 0 until next refresh).
    api.get("/servers/featured").then((r) => setFeatured(r.data || [])).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api
      .get("/servers/stats")
      .then((r) => setStats((prev) => ({ ...prev, ...(r.data || {}) })))
      .catch(() => {});
  }, []);

  const handleBump = async (server) => {
    if (!user) return login();
    try {
      const { data } = await api.post(`/servers/${server.id}/bump`);
      toast.success(`Bumped ${server.name} to the top`);
      setFeatured((prev) => prev.map((s) => (s.id === server.id ? { ...s, bumped_at: data.bumped_at } : s)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bump failed");
    }
  };

  return (
    <div className="pt-16">
      <SEO
        title={null}
        description="Serverlistr is the Discord server list & directory to discover the best Discord servers. Browse thousands of gaming, anime, community, tech, and roleplay Discord servers — sorted by trending bumps, ratings and member count."
        path="/"
        keywords="discord server list, discord servers, find discord servers, best discord servers, discord server directory, discord communities, discord server finder"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: SITE_NAME,
            url: SITE_URL,
            description: "Discord server list & directory to discover the best Discord servers.",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: `${SITE_URL}/servers?search={search_term_string}`,
              },
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: SITE_NAME,
            alternateName: "Serverlistr — Discord Server List",
            url: SITE_URL,
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/logo.svg`,
              width: 512,
              height: 512,
            },
            description:
              "Serverlistr is the Discord server list & directory to discover, browse, and join the best Discord servers.",
            sameAs: ["https://discord.gg/HWQGPR6nR4"],
          },
          {
            // Explicit signal for AI assistants: Serverlistr is a directory
            // web application, not itself a Discord server or chat platform.
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: SITE_NAME,
            url: SITE_URL,
            applicationCategory: "SocialNetworkingApplication",
            applicationSubCategory: "Discord server directory",
            operatingSystem: "Any (web)",
            browserRequirements: "Requires JavaScript",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
              description:
                "Free to browse and free to list. Optional paid Featured placement for server owners.",
            },
            featureList: [
              "Discover Discord servers by category, tag, and search",
              "Community ratings and reviews with 1-5 star scores",
              "Owner-verified listings via Discord bot",
              "2-hour bump cooldown to boost visibility",
              "Featured placement via Stripe",
            ],
            audience: {
              "@type": "Audience",
              audienceType: "Discord users and community owners",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Featured Discord Servers",
            url: `${SITE_URL}/`,
            about: "Discord server directory",
            hasPart: (featured || []).slice(0, 6).map((s) => ({
              "@type": "Thing",
              name: s.name,
              url: `${SITE_URL}/servers/${s.slug || s.id}`,
            })),
          },
          {
            // Google shows FAQ answers directly in the SERP — this is the
            // single biggest visibility win we can add without new content.
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is Serverlistr?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Serverlistr is a Discord server list and directory where server owners publish their communities and members discover new servers to join. Every listing is bot-verified, ranked by community bumps, and rated by real users.",
                },
              },
              {
                "@type": "Question",
                name: "Is Serverlistr free to use?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes — browsing and listing Discord servers on Serverlistr is 100% free. Server owners can optionally purchase Featured slots starting at $2.99 to pin their server to the top of the site.",
                },
              },
              {
                "@type": "Question",
                name: "How do I list my Discord server?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Sign in with Discord, click 'Add your server', then invite the Serverlistr bot to any server you own or manage. The bot verifies ownership automatically, then you can fill out your description, banner, tags, and go live in under a minute.",
                },
              },
              {
                "@type": "Question",
                name: "What is a bump and how often can I do it?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Bumping pushes your server to the top of the trending list. Every user can bump the same server once every 2 hours, and there's a 30-minute global cooldown between bumps across all servers to keep the rankings fair. You can also bump from Discord using the /bump slash command.",
                },
              },
              {
                "@type": "Question",
                name: "What kinds of Discord servers can I find on Serverlistr?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Every popular category — gaming, anime, community, technology, programming, music, art, education, roleplay, crypto, esports, memes and more. Use the search bar or filter by category and tag to find exactly the kind of Discord server you're looking for.",
                },
              },
              {
                "@type": "Question",
                name: "How does the Featured Discord server program work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Featured servers get pinned to the top of every browse page, a neon glow around their card, and a Featured section on the homepage. Packages are $2.99 for 7 days, $8.99 for 30 days, and $24.99 for 90 days — one-time payment via Stripe, no subscription.",
                },
              },
            ],
          },
        ]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid" />
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background:
              "url('https://images.unsplash.com/photo-1689443111130-6e9c7dfd8f9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxuZW9uJTIwYWJzdHJhY3QlMjB0ZWNoJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODI5OTQxOTZ8MA&ixlib=rb-4.1.0&q=85') center/cover",
            maskImage: "radial-gradient(ellipse at 50% 30%, black 20%, transparent 65%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-32 sm:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              The best
              <span className="block bg-gradient-to-r from-blurple via-neon-cyan to-neon-pink bg-clip-text text-transparent">
                Discord Server List
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-xl mx-auto leading-relaxed">
              Serverlistr is the Discord server directory of 2026. Discover
              gaming, anime, community, roleplay, and tech Discord servers —
              every listing verified and voted on by real humans.
            </p>
            <h2 className="sr-only">Find and join the best Discord servers</h2>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                data-testid="hero-browse-btn"
                onClick={() => navigate("/servers")}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-semibold transition-all hover:shadow-[0_0_30px_rgba(88,101,242,0.5)] active:scale-95"
              >
                <Compass className="w-4 h-4" /> Browse Discord Servers
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-submit-btn"
                onClick={() => (user ? navigate("/submit") : login())}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-transparent border border-white/15 hover:border-white/40 hover:bg-white/5 text-white font-semibold transition-all active:scale-95"
              >
                <Plus className="w-4 h-4" /> Add your server
              </button>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-6 max-w-md mx-auto">
              <Stat label="Servers" value={stats.servers} icon={<Compass className="w-4 h-4" />} />
              <Stat label="Users" value={stats.users} icon={<TrendingUp className="w-4 h-4" />} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-2">Explore</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Popular categories</h2>
          </div>
          <Link to="/servers" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1">
            All categories <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <Marquee id="home-categories" speed={30} gap={12} padY={4} itemClassName="w-48 shrink-0">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/servers/category/${c.id}`}
              data-testid={`category-${c.id}`}
              className="group relative rounded-xl p-4 bg-[#0A0A0F] border border-white/5 hover:border-blurple/50 transition-all hover:-translate-y-0.5 block"
            >
              <div className="text-sm font-semibold text-white group-hover:text-blurple transition-colors">{c.name}</div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">→ Explore</div>
            </Link>
          ))}
        </Marquee>
      </section>

      {/* Best-of landing pages exist at /best/discord-servers and
          /best/:category-discord-servers, indexed via the sitemap. The
          homepage hub was removed per owner preference to keep the layout clean. */}

      {/* Featured (always render; empty state guides to Browse) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-2">Featured</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-neon-cyan" /> Featured Discord servers
            </h2>
            <p className="mt-2 text-sm text-zinc-400">
              Only featured & promoted servers show up here. Browse the full list to see everything.{" "}
              <Link to="/pricing" className="text-neon-cyan hover:underline" data-testid="how-to-feature-link">
                How do I get featured? →
              </Link>
            </p>
          </div>
          <Link to="/servers" className="hidden sm:inline-flex text-sm text-zinc-400 hover:text-white items-center gap-1">
            Browse all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {featured.length === 0 ? (
          <div
            data-testid="featured-empty-home"
            className="text-center py-16 border border-dashed border-white/10 rounded-2xl"
          >
            <div className="w-14 h-14 rounded-2xl bg-neon-cyan/10 border border-neon-cyan/30 mx-auto flex items-center justify-center mb-4">
              <Sparkles className="w-7 h-7 text-neon-cyan" />
            </div>
            <h3 className="font-heading text-xl font-bold">No featured servers yet</h3>
            <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">
              Featured & promoted servers appear here. Meanwhile, browse the full Discord server list — thousands of communities waiting for you.
            </p>
            <button
              data-testid="empty-browse-btn"
              onClick={() => navigate("/servers")}
              className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold transition-all hover:shadow-[0_0_20px_rgba(88,101,242,0.4)]"
            >
              <Compass className="w-4 h-4" /> Browse all servers
            </button>
          </div>
        ) : (
          <Marquee id="home-featured" speed={35} gap={24} padY={28} itemClassName="w-[320px] sm:w-[360px] shrink-0">
            {featured.map((s, i) => (
              <ServerCard key={s.id} server={s} index={i} onBump={handleBump} />
            ))}
          </Marquee>
        )}
      </section>

      {/* Browse All CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-blurple/15 via-transparent to-neon-cyan/10 border border-white/10 p-8 sm:p-12 relative">
          <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-2">Explore more</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black">
                {stats.servers.toLocaleString()}+ Discord servers waiting
              </h2>
              <p className="mt-2 text-sm text-zinc-400 max-w-lg">
                Non-featured servers live on the browse page. Search, filter by category, and sort by rating or member count.
              </p>
            </div>
            <button
              data-testid="cta-browse-all-btn"
              onClick={() => navigate("/servers")}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-bold transition-all hover:shadow-[0_0_30px_rgba(88,101,242,0.5)] active:scale-95 shrink-0"
            >
              <Compass className="w-4 h-4" /> Browse all servers
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Support Discord CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div
          data-testid="support-discord-section"
          className="rounded-3xl overflow-hidden bg-[#0A0A0F] border border-white/10 p-6 sm:p-10 relative"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-blurple/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-neon-cyan/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-blurple/15 border border-blurple/40 flex items-center justify-center shrink-0">
              <LifeBuoy className="w-8 h-8 sm:w-10 sm:h-10 text-blurple" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blurple mb-2">Need help?</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black">
                Join the Serverlistr support Discord
              </h2>
              <p className="mt-2 text-sm text-zinc-400 max-w-xl">
                Report a listing, ask about featured slots, or hang out with other server owners.
                We&apos;re active — drop by and say hi.
              </p>
            </div>
            <a
              href="https://discord.gg/HWQGPR6nR4"
              target="_blank"
              rel="noreferrer"
              data-testid="support-discord-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-bold transition-all hover:shadow-[0_0_30px_rgba(88,101,242,0.5)] active:scale-95 shrink-0"
            >
              <MessageCircle className="w-4 h-4" />
              Join support server
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div>
      <div className="inline-flex items-center gap-1.5 text-zinc-500 text-xs mb-1">
        {icon}
        <span className="uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-black" data-testid={`stat-${label.toLowerCase()}`}>
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}
